import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAdminConversationsApi,
  getAdminMessagesApi,
  sendAdminMessageApi,
  getPinnedMessagesApi,
} from "../../api/messagingApi";

export const fetchAdminConversations = createAsyncThunk(
  "messaging/fetchConversations",
  async (_, thunkAPI) => {
    try {
      const data = await getAdminConversationsApi();
      return data.conversations;
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

export const fetchAdminMessages = createAsyncThunk(
  "messaging/fetchMessages",
  async ({ conversationId, page = 1 }, thunkAPI) => {
    try {
      const data = await getAdminMessagesApi(conversationId, page);
      return { messages: data.messages, conversationId };
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

export const fetchMoreAdminMessages = createAsyncThunk(
  "messaging/fetchMoreMessages",
  async ({ conversationId, page }, thunkAPI) => {
    try {
      const data = await getAdminMessagesApi(conversationId, page);
      return { messages: data.messages, conversationId };
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

export const fetchPinnedMessages = createAsyncThunk(
  "messaging/fetchPinnedMessages",
  async ({ conversationId, page = 1 }, thunkAPI) => {
    try {
      const data = await getPinnedMessagesApi(page);
      return { messages: data.messages, conversationId: data.conversationId || conversationId };
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

export const fetchMorePinnedMessages = createAsyncThunk(
  "messaging/fetchMorePinnedMessages",
  async ({ conversationId, page }, thunkAPI) => {
    try {
      const data = await getPinnedMessagesApi(page);
      return { messages: data.messages, conversationId: data.conversationId || conversationId };
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

export const sendAdminMessage = createAsyncThunk(
  "messaging/sendMessage",
  async (payload, thunkAPI) => {
    try {
      const data = await sendAdminMessageApi(payload);
      return data.message;
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

const messagingSlice = createSlice({
  name: "messaging",
  initialState: {
    conversations: [],
    activeConversationId: null,
    messages: {},
    hasMore: {},
    loadingMore: {},
    totalUnread: 0,
    loading: false,
    sendingMessage: false,
    needsRefetch: false,
    error: null,
  },
  reducers: {
    setActiveConversation(state, action) {
      state.activeConversationId = action.payload;
    },
    appendMessage(state, action) {
      const msg = action.payload;
      const convId = msg.conversationId;
      if (!state.messages[convId]) state.messages[convId] = [];
      const exists = state.messages[convId].find((m) => m._id === msg._id);
      if (!exists) state.messages[convId].push(msg);

      const idx = state.conversations.findIndex((c) => c._id === convId);
      if (idx >= 0) {
        const conv = state.conversations[idx];
        conv.lastMessage = msg.content || (msg.attachmentType === "image" ? "📷 Image" : "");
        conv.lastMessageAt = msg.createdAt;
        if (convId !== state.activeConversationId) {
          if (conv.isPinnedSuperAdmin) {
            // In the pinned conv coach admin is the user — super admin's messages are 'admin' type
            if (msg.senderType === "admin") conv.userUnreadCount = (conv.userUnreadCount || 0) + 1;
          } else {
            if (msg.senderType === "user") conv.adminUnreadCount = (conv.adminUnreadCount || 0) + 1;
          }
        }
        // Keep pinned conv always first, bubble others to top
        if (!conv.isPinnedSuperAdmin && idx > 0 && !state.conversations[0].isPinnedSuperAdmin) {
          state.conversations.splice(idx, 1);
          state.conversations.unshift(conv);
        } else if (!conv.isPinnedSuperAdmin && idx > 1) {
          state.conversations.splice(idx, 1);
          state.conversations.splice(1, 0, conv);
        }
      } else {
        // Unknown conversation — flag a refetch
        state.needsRefetch = true;
      }
      state.totalUnread = state.conversations.reduce(
        (acc, c) => acc + (c.isPinnedSuperAdmin ? (c.userUnreadCount || 0) : (c.adminUnreadCount || 0)), 0
      );
    },
    clearNeedsRefetch(state) {
      state.needsRefetch = false;
    },
    clearUnreadForConversation(state, action) {
      const convId = action.payload;
      const conv = state.conversations.find((c) => c._id === convId);
      if (conv) { conv.adminUnreadCount = 0; conv.userUnreadCount = 0; }
      state.totalUnread = state.conversations.reduce(
        (acc, c) => acc + (c.isPinnedSuperAdmin ? (c.userUnreadCount || 0) : (c.adminUnreadCount || 0)), 0
      );
    },
    updateConversationEnabled(state, action) {
      const { conversationId, isEnabled } = action.payload;
      const conv = state.conversations.find((c) => c._id === conversationId);
      if (conv) conv.isEnabled = isEnabled;
    },
    updateMessageStatus(state, action) {
      const { conversationId, messageIds, status, senderType } = action.payload;
      const msgs = state.messages[conversationId];
      if (!msgs) return;
      const idSet = new Set(messageIds);
      // Determine which field to update based on who sent the message:
      //   - admin sent it → mark deliveredToUser/readByUser (recipient = user)
      //   - user sent it  → mark deliveredToAdmin/readByAdmin (recipient = admin)
      // Falls back to admin-as-sender for older payloads without senderType.
      state.messages[conversationId] = msgs.map((m) => {
        if (!idSet.has(m._id)) return m;
        const sType = senderType || m.senderType || 'admin';
        if (sType === 'user') {
          if (status === 'delivered') return { ...m, deliveredToAdmin: true };
          if (status === 'read') return { ...m, deliveredToAdmin: true, readByAdmin: true };
        } else {
          if (status === 'delivered') return { ...m, deliveredToUser: true };
          if (status === 'read') return { ...m, deliveredToUser: true, readByUser: true };
        }
        return m;
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminConversations.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAdminConversations.fulfilled, (state, action) => {
        state.loading = false;
        const sorted = [...action.payload].sort((a, b) => {
          if (a.isPinnedSuperAdmin && !b.isPinnedSuperAdmin) return -1;
          if (!a.isPinnedSuperAdmin && b.isPinnedSuperAdmin) return 1;
          const ta = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
          const tb = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
          return tb - ta;
        });
        state.conversations = sorted;
        state.needsRefetch = false;
        state.totalUnread = sorted.reduce(
          (acc, c) => acc + (c.isPinnedSuperAdmin ? (c.userUnreadCount || 0) : (c.adminUnreadCount || 0)), 0
        );
      })
      .addCase(fetchAdminConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAdminMessages.fulfilled, (state, action) => {
        const { messages, conversationId } = action.payload;
        state.messages[conversationId] = messages;
        state.hasMore[conversationId] = messages.length >= 50;
      })
      .addCase(fetchMoreAdminMessages.pending, (state, action) => {
        state.loadingMore[action.meta.arg.conversationId] = true;
      })
      .addCase(fetchMoreAdminMessages.fulfilled, (state, action) => {
        const { messages, conversationId } = action.payload;
        state.loadingMore[conversationId] = false;
        state.hasMore[conversationId] = messages.length >= 50;
        if (messages.length > 0) {
          const existing = state.messages[conversationId] || [];
          const existingIds = new Set(existing.map((m) => m._id));
          const newOnes = messages.filter((m) => !existingIds.has(m._id));
          state.messages[conversationId] = [...newOnes, ...existing];
        }
      })
      .addCase(fetchMoreAdminMessages.rejected, (state, action) => {
        state.loadingMore[action.meta.arg.conversationId] = false;
      })
      .addCase(fetchPinnedMessages.fulfilled, (state, action) => {
        const { messages, conversationId } = action.payload;
        state.messages[conversationId] = messages;
        state.hasMore[conversationId] = messages.length >= 50;
      })
      .addCase(fetchMorePinnedMessages.pending, (state, action) => {
        state.loadingMore[action.meta.arg.conversationId] = true;
      })
      .addCase(fetchMorePinnedMessages.fulfilled, (state, action) => {
        const { messages, conversationId } = action.payload;
        state.loadingMore[conversationId] = false;
        state.hasMore[conversationId] = messages.length >= 50;
        if (messages.length > 0) {
          const existing = state.messages[conversationId] || [];
          const existingIds = new Set(existing.map((m) => m._id));
          const newOnes = messages.filter((m) => !existingIds.has(m._id));
          state.messages[conversationId] = [...newOnes, ...existing];
        }
      })
      .addCase(fetchMorePinnedMessages.rejected, (state, action) => {
        state.loadingMore[action.meta.arg.conversationId] = false;
      })
      .addCase(sendAdminMessage.pending, (state) => {
        state.sendingMessage = true;
      })
      .addCase(sendAdminMessage.fulfilled, (state, action) => {
        state.sendingMessage = false;
        const msg = action.payload;
        const convId = msg.conversationId;
        if (!state.messages[convId]) state.messages[convId] = [];
        const exists = state.messages[convId].find((m) => m._id === msg._id);
        if (!exists) state.messages[convId].push(msg);
      })
      .addCase(sendAdminMessage.rejected, (state) => {
        state.sendingMessage = false;
      });
  },
});

export const {
  setActiveConversation,
  appendMessage,
  clearUnreadForConversation,
  updateConversationEnabled,
  clearNeedsRefetch,
  updateMessageStatus,
} = messagingSlice.actions;


export default messagingSlice.reducer;
